/**
 * orchestrator — the typed agent runtime.
 *
 * Reads codegen.json#agents (the registry of specialist agents) and
 * dispatches each typed graph subject to every applicable agent,
 * enforcing per-agent SLA:
 *   - muteBelow         · confidence floor (drops proposals below this)
 *   - pulseAbove        · confidence ceiling (marks high-confidence pulses)
 *   - cooldownSec       · minimum seconds between fires for the same
 *                         (agent, subject) pair — Memory-Card decay
 *   - accept-rate       · running counter the cockpit surfaces in /admin
 *
 * One agent = one job. The orchestrator owns the routing, the per-agent
 * memory (lastFireAt + fires + accepts + rejects), and the registry
 * iteration. CLAUDE.md §1 — registry lives in the ontology, NOT inline.
 */
import codegen from "../../../../docs/ontology/codegen.json";
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import type { AgentProposal } from "./types";
import {
  detectStyleCohesion, detectBudgetPull, detectLeadRisk,
  detectKitchenWorkTriangle, detectVastuMismatch, detectMissingApproval,
  detectSpacingDensity, detectOrientationGuidance,
} from "./agentSuggestions";

export interface AgentRegistration {
  readonly id: string;
  readonly label: string;
  readonly expert: string;
  readonly scope: ReadonlyArray<string>;
  readonly produces: ReadonlyArray<string>;
  readonly citation: string;
  readonly muteBelow: number;
  readonly pulseAbove: number;
  readonly cooldownSec: number;
  readonly fn: string;
  readonly note?: string;
}

/** Per-(graph, room) agent function. Every specialist exposes the same
 *  signature so the orchestrator can dispatch uniformly. */
type PerRoomAgentFn = (graph: ProjectGraph, room: GraphNode, nowIso: string) => AgentProposal[];

/** Mapping from registry `fn` string → actual implementation. Functions
 *  with non-per-room signatures (wouldFit on a candidate, scheduleSynth
 *  on a graph) are NOT in this map — they're dispatched from their own
 *  surfaces and reuse the SLA via `consult()` below. */
const PER_ROOM_AGENTS: Readonly<Record<string, PerRoomAgentFn>> = {
  detectStyleCohesion,
  detectBudgetPull,
  detectLeadRisk,
  // The remaining detectors take (room, now) only — wrap so they share
  // the uniform signature.
  detectKitchenWorkTriangle: (_g, room, now) => detectKitchenWorkTriangle(room, now),
  detectVastuMismatch:       (_g, room, now) => detectVastuMismatch(room, now),
  detectMissingApproval,
  detectSpacingDensity,
  detectOrientationGuidance,
};

export function agentRegistry(): ReadonlyArray<AgentRegistration> {
  return (codegen.agents ?? []) as ReadonlyArray<AgentRegistration>;
}

export function getAgent(id: string): AgentRegistration | undefined {
  return agentRegistry().find((a) => a.id === id);
}

// ─── Per-agent memory ──────────────────────────────────────────────────

interface AgentMemory {
  /** subjectId → ms timestamp of last successful emission. */
  readonly lastFireAt: Map<string, number>;
  fires: number;
  accepts: number;
  rejects: number;
  /** Proposals that were muted because confidence was below the agent's
   *  muteBelow floor. Useful telemetry for tuning thresholds. */
  muted: number;
}

const AGENT_MEMORY = new Map<string, AgentMemory>();

function memoryFor(agentId: string): AgentMemory {
  let m = AGENT_MEMORY.get(agentId);
  if (!m) {
    m = { lastFireAt: new Map<string, number>(), fires: 0, accepts: 0, rejects: 0, muted: 0 };
    AGENT_MEMORY.set(agentId, m);
  }
  return m;
}

// ─── Public API ─────────────────────────────────────────────────────────

/** Dispatch every applicable per-room agent across every room. The
 *  returned proposals are already filtered through each agent's
 *  muteBelow + cooldownSec SLA, and tagged with `agentId` + a
 *  provenance citation like `agent/style-cohesion`. */
export function orchestrate(graph: ProjectGraph, rooms: ReadonlyArray<GraphNode>): AgentProposal[] {
  const now = new Date().toISOString();
  const nowMs = Date.now();
  const out: AgentProposal[] = [];
  for (const reg of agentRegistry()) {
    const fn = PER_ROOM_AGENTS[reg.fn];
    if (!fn) continue; // non-per-room agents (fit-check, schedule-synth) dispatched from their own UIs
    const mem = memoryFor(reg.id);
    for (const room of rooms) {
      const subjectKey = `${reg.id}:${room.id}`;
      const lastFire = mem.lastFireAt.get(subjectKey) ?? 0;
      if (nowMs - lastFire < reg.cooldownSec * 1000) continue;
      let emitted = 0;
      for (const p of fn(graph, room, now)) {
        if (p.confidence < reg.muteBelow) {
          mem.muted += 1;
          continue;
        }
        out.push({
          ...p,
          agentId: reg.id,
          wasInformedBy: [...p.wasInformedBy, `agent/${reg.id}`],
        });
        emitted += 1;
        mem.fires += 1;
      }
      if (emitted > 0) mem.lastFireAt.set(subjectKey, nowMs);
    }
  }
  return out;
}

/** Consult a single registered agent for telemetry / SLA enforcement
 *  from a non-per-room dispatch (e.g. wouldFit on a catalog click,
 *  synthesizeSchedule from the Gantt CTA). Records the fire against
 *  the agent's memory so /admin agent dashboards stay honest. */
export function consult(agentId: string, subjectId: string): { allowedNow: boolean; reason: string } {
  const reg = getAgent(agentId);
  if (!reg) return { allowedNow: true, reason: "agent not registered" };
  const mem = memoryFor(agentId);
  const lastFire = mem.lastFireAt.get(`${agentId}:${subjectId}`) ?? 0;
  const elapsedMs = Date.now() - lastFire;
  if (elapsedMs < reg.cooldownSec * 1000) {
    return { allowedNow: false, reason: `cooldown ${Math.ceil((reg.cooldownSec * 1000 - elapsedMs) / 1000)}s remaining` };
  }
  return { allowedNow: true, reason: "ok" };
}

/** Record that a fired Suggestion was accepted by the user (cockpit
 *  click-accept). Drives the per-agent accept-rate visible in /admin. */
export function recordAccept(agentId: string): void {
  memoryFor(agentId).accepts += 1;
}

/** Record that a fired Suggestion was rejected (dismissed). */
export function recordReject(agentId: string): void {
  memoryFor(agentId).rejects += 1;
}

export interface AgentSnapshotRow {
  readonly id: string;
  readonly label: string;
  readonly expert: string;
  readonly fires: number;
  readonly accepts: number;
  readonly rejects: number;
  readonly muted: number;
  readonly acceptRate: number;   // 0..1
  readonly muteBelow: number;
  readonly pulseAbove: number;
  readonly cooldownSec: number;
}

/** Snapshot every registered agent's runtime stats. The /admin → Agents
 *  page renders this; nothing else should read AGENT_MEMORY directly. */
export function agentSnapshot(): ReadonlyArray<AgentSnapshotRow> {
  return agentRegistry().map((reg) => {
    const m = AGENT_MEMORY.get(reg.id);
    const fires = m?.fires ?? 0;
    const accepts = m?.accepts ?? 0;
    return {
      id: reg.id,
      label: reg.label,
      expert: reg.expert,
      fires,
      accepts,
      rejects: m?.rejects ?? 0,
      muted: m?.muted ?? 0,
      acceptRate: fires === 0 ? 0 : accepts / fires,
      muteBelow: reg.muteBelow,
      pulseAbove: reg.pulseAbove,
      cooldownSec: reg.cooldownSec,
    };
  });
}

/** Test-only — reset per-agent memory. Never call from production code. */
export function __resetAgentMemoryForTests(): void {
  AGENT_MEMORY.clear();
}
